import { until } from "@vueuse/core";

import { computed, watch } from "vue";

import { useWorktreeManager } from "@/composables/worktree-manager";
import { type DomainInTree } from "@/stores/domains-store";

import { DomainEntity } from ".";
import { BaseLinkedEntitiesManager } from "../mystekz-linked-entities/manager";

/**
 * Manages the business entities and replaces
 */
export class DomainsManager extends BaseLinkedEntitiesManager<DomainEntity> {
    nodeType = "domain" as const;
    worktreeManager = useWorktreeManager();

    constructor(baseUrl: string) {
        super(baseUrl);
        const isLoading = computed(() => {
            return (
                this.worktreeManager.isFetching.value ||
                this.organizationManager.isLoadingOrganizations.value ||
                this.stekManager.isLoadingSteks.value
            );
        });
        this.ready = until(isLoading).not.toBeTruthy();

        watch(
            this.worktreeManager.worktree,
            (newWorktree) => {
                if (!newWorktree) {
                    this.setEntities([]);
                    return;
                }

                this.setEntities(newWorktree.domains);
            },
            { immediate: true }
        );
    }

    itemToEntity(domain: DomainInTree): DomainEntity {
        const orgName = this.organizationManager.organization.value?.name;
        const stekName = this.stekManager.stek.value?.name;

        const linkedProducts = [...domain.products]
            .sort(
                (a, b) =>
                    (a.order_index ?? 0) - (b.order_index ?? 0) ||
                    a.name.localeCompare(b.name)
            )
            .map((product) => {
                const linkedProcesses = [...product.processes]
                    .sort(
                        (a, b) =>
                            (a.order_index ?? 0) - (b.order_index ?? 0) ||
                            a.name.localeCompare(b.name)
                    )
                    .map((process) => ({
                        name: process.name,
                        id: process.identity,
                        stekName,
                        orgName,
                        type: "process" as const,
                        href: encodeURI(
                            (
                                this.baseUrl +
                                "/" +
                                orgName +
                                "/steks/" +
                                stekName +
                                "/process/" +
                                process.identity
                            ).replace(/([^:])\/{2,}/g, "$1/")
                        ),
                        state: "ready" as const,
                        data: process,
                    }));

                return {
                    name: product.name,
                    id: product.identity,
                    stekName,
                    orgName,
                    type: "product" as const,
                    href: encodeURI(
                        (
                            this.baseUrl +
                            "/" +
                            orgName +
                            "/steks/" +
                            stekName +
                            "/product/" +
                            product.identity
                        ).replace(/([^:])\/{2,}/g, "$1/")
                    ),
                    state: "ready" as const,
                    data: product,
                    linkedProcesses,
                };
            });

        const entity = {
            name: domain.name,
            id: domain.identity,
            order_index: domain.order_index,
            stekName,
            orgName,
            type: this.nodeType,
            data: {
                ...domain,
                linkedProducts,
            },
            state: "ready",
        } as DomainEntity;
        entity.href = this.getHref(entity);
        return entity;
    }
}
